import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CalendarPlus,
  Eye,
  Pencil,
  XCircle,
  Loader2,
  ExternalLink,
  Search,
  X,
  Calendar,
  PlayCircle,
  CheckCircle2,
  ListTodo,
  ChevronDown,
  Clock,
  Hash,
  UserCheck,
  Info,
  RefreshCw,
  WifiOff,
  BookOpen,
  Sparkles,
  Zap,
  AlertCircle,
} from 'lucide-react';
import * as apiClient from '@/services/apiClient';
import { useAuth, hasPermission } from '@/context/AuthContext';
import { ReGenerateQuestionsModal } from '@/components/questionsets/ReGenerateQuestionsModal';
import ResendInvitePanel from '@/components/schedule/ResendInvitePanel';
import DashboardKpiCard from '@/components/ux/DashboardKpiCard';
import StatusBadge from '@/components/ux/StatusBadge';
import { NativeSelect } from '@/components/ux/NativeSelect';
import Pagination from '@/components/ui/Pagination';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

function toDateInputValue(d) {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}

function toTimeInputValue(d) {
  const x = new Date(d);
  return `${String(x.getHours()).padStart(2, '0')}:${String(x.getMinutes()).padStart(2, '0')}`;
}

function combineDateTimeLocal(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const [y, m, day] = dateStr.split('-').map(Number);
  const [hh, mm] = timeStr.split(':').map(Number);
  return new Date(y, m - 1, day, hh, mm, 0, 0);
}

function minDateToday() {
  return toDateInputValue(new Date());
}

/** Map API question rows for schedule preview (always boolean _included). */
// ── SearchableSelect ──────────────────────────────────────────────────────────
// Dropdown with inline search filter. Works for any list of { value, label, sub? } options.
function SearchableSelect({ id, label, value, onChange, options, placeholder = 'Select…', disabled = false, required = false, emptyMessage = 'No options found.' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return options;
    return options.filter(o =>
      o.label.toLowerCase().includes(q) || (o.sub || '').toLowerCase().includes(q)
    );
  }, [options, query]);

  const selected = options.find(o => String(o.value) === String(value));

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="space-y-1.5" ref={ref}>
      {label && <Label htmlFor={id}>{label}{required && <span className="text-destructive ml-0.5">*</span>}</Label>}
      <div className="relative">
        <button
          id={id}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen(v => !v)}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm ring-offset-background',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            open && 'ring-2 ring-ring ring-offset-2',
            !selected && 'text-muted-foreground',
          )}
        >
          <span className="truncate">
            {selected ? (
              <span>
                {selected.label}
                {selected.sub && <span className="text-muted-foreground ml-1.5 text-xs">({selected.sub})</span>}
              </span>
            ) : placeholder}
          </span>
          <ChevronDown className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </button>

        {open && (
          <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg">
            {/* Search box */}
            <div className="flex items-center gap-2 border-b px-3 py-2">
              <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <input
                autoFocus
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                placeholder="Search…"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
              {query && (
                <button type="button" onClick={() => setQuery('')} className="text-muted-foreground hover:text-foreground">
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
            {/* Options */}
            <div className="max-h-52 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <p className="px-3 py-4 text-center text-xs text-muted-foreground">{emptyMessage}</p>
              ) : (
                filtered.map(o => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => handleSelect(o.value)}
                    className={cn(
                      'flex w-full flex-col items-start px-3 py-2 text-sm hover:bg-muted/60 transition-colors',
                      String(value) === String(o.value) && 'bg-primary/10 font-medium',
                    )}
                  >
                    <span>{o.label}</span>
                    {o.sub && <span className="text-xs text-muted-foreground">{o.sub}</span>}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EditInterviewDialog({ open, onClose, session, templates, candidates, onUpdate }) {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState('');
  const [dateStr, setDateStr] = useState('');
  const [timeStr, setTimeStr] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successInfo, setSuccessInfo] = useState(null);

  useEffect(() => {
    if (session && open) {
      setSelectedTemplate(session.template?._id || '');
      setSelectedCandidate(session.candidate?._id || '');
      const scheduled = new Date(session.scheduledAt);
      setDateStr(toDateInputValue(scheduled));
      setTimeStr(toTimeInputValue(scheduled));
      setError('');
      setSuccessInfo(null);
    }
  }, [session, open]);

  const handleClose = () => {
    if (!isLoading) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!session) return;
    if (!selectedTemplate || !selectedCandidate) {
      setError('Please select a template and a candidate.');
      return;
    }
    const dt = combineDateTimeLocal(dateStr, timeStr);
    if (!dt || dt < new Date()) {
      setError('Scheduled date and time cannot be in the past.');
      return;
    }
    const scheduledAt = dt.toISOString();
    setIsLoading(true);
    setError('');
    setSuccessInfo(null);
    try {
      await apiClient.updateSession(session._id, {
        templateId: selectedTemplate,
        candidateId: selectedCandidate,
        scheduledAt,
      });
      setSuccessInfo({ message: 'Interview updated. Candidate has been notified by email.' });
      setTimeout(() => onUpdate?.(), 2000);
    } catch (err) {
      setError(err.message || 'Failed to update interview.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!session) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit interview</DialogTitle>
          <DialogDescription>Update template, candidate, or schedule.</DialogDescription>
        </DialogHeader>
        <form id="edit-interview-form" onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}
          {successInfo && (
            <div role="status" className="rounded-md border border-success/40 bg-success/10 px-4 py-3 text-sm text-success">
              {successInfo.message}
            </div>
          )}
          <NativeSelect
            id="edit-template"
            label="Interview template"
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            required
            disabled={isLoading}
          >
            {templates.map((t) => (
              <option key={t._id} value={t._id}>
                {t.title}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect
            id="edit-candidate"
            label="Candidate"
            value={selectedCandidate}
            onChange={(e) => setSelectedCandidate(e.target.value)}
            required
            disabled={isLoading}
          >
            {candidates.map((c) => (
              <option key={c._id} value={c._id}>
                {c.firstName} {c.lastName} ({c.email})
              </option>
            ))}
          </NativeSelect>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="edit-date">Date</Label>
              <Input
                id="edit-date"
                type="date"
                min={minDateToday()}
                value={dateStr}
                onChange={(e) => setDateStr(e.target.value)}
                required
                disabled={isLoading}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-time">Time</Label>
              <Input
                id="edit-time"
                type="time"
                step={300}
                value={timeStr}
                onChange={(e) => setTimeStr(e.target.value)}
                required
                disabled={isLoading}
                className="h-11"
              />
            </div>
          </div>
        </form>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" form="edit-interview-form" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                Saving…
              </>
            ) : (
              'Update'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelInterviewDialog({ open, onClose, session, onCancel }) {
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    if (!isLoading) {
      setReason('');
      setError('');
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!session) return;
    setIsLoading(true);
    setError('');
    try {
      await apiClient.cancelSession(session._id, reason);
      setTimeout(() => onCancel?.(), 1000);
    } catch (err) {
      setError(err.message || 'Failed to cancel interview.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!session) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-destructive">Cancel interview</DialogTitle>
          <DialogDescription>The candidate will be notified by email.</DialogDescription>
        </DialogHeader>
        {error && (
          <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">Candidate:</span>{' '}
            {session.candidate?.firstName} {session.candidate?.lastName}
          </p>
          <p>
            <span className="font-medium text-foreground">Template:</span> {session.template?.title}
          </p>
          <p>
            <span className="font-medium text-foreground">Scheduled:</span>{' '}
            {new Date(session.scheduledAt).toLocaleString()}
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="cancel-reason">Cancellation reason (optional)</Label>
          <textarea
            id="cancel-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            disabled={isLoading}
            className={cn(
              'flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
            )}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
            Keep interview
          </Button>
          <Button type="button" variant="destructive" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                Cancelling…
              </>
            ) : (
              'Cancel interview'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewInterviewDialog({ open, onClose, session, onRegenerated }) {
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [localSession,   setLocalSession]   = useState(null);

  React.useEffect(() => { if (session) setLocalSession(session); }, [session]);

  const displaySession = localSession || session;
  if (!displaySession) return null;

  const isScheduled = displaySession.status === 'scheduled';
  const questions   = displaySession.questions || [];

  const handleRegenerated = (updatedSession) => {
    setLocalSession(updatedSession);
    setShowRegenModal(false);
    onRegenerated?.(updatedSession);
  };

  const fmtDate = (d) => d
    ? new Date(d).toLocaleString('en-IN', {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
      })
    : '—';

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* Wide dialog — side-by-side layout so questions are always visible */}
      <DialogContent className="max-h-[92vh] w-[95vw] overflow-hidden flex flex-col sm:max-w-5xl p-0">

        {/* ── Header ────────────────────────────────────────────────────────── */}
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-border flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="text-base">Interview Details</DialogTitle>
              <DialogDescription className="text-xs mt-0.5">
                {displaySession.candidate?.firstName} {displaySession.candidate?.lastName}
                {displaySession.template?.title ? ` · ${displaySession.template.title}` : ''}
                {displaySession.roundName ? ` · ${displaySession.roundName}` : ''}
              </DialogDescription>
            </div>
            <StatusBadge status={displaySession.status} />
          </div>
        </DialogHeader>

        {/* ── Body: split layout ─────────────────────────────────────────────── */}
        <div className="flex flex-1 overflow-hidden divide-x divide-border">

          {/* Left: session meta ──────────────────────────────────────────────── */}
          <div className="w-64 shrink-0 overflow-y-auto p-5 space-y-4">
            {[
              { label: 'Candidate',   value: `${displaySession.candidate?.firstName || ''} ${displaySession.candidate?.lastName || ''}`.trim() || '—' },
              { label: 'Email',       value: displaySession.candidate?.email || '—' },
              { label: 'Job',         value: displaySession.template?.title  || '—' },
              { label: 'Scheduled',   value: fmtDate(displaySession.scheduledAt) },
              // The link is valid across a window, not just at the slot — the
              // team was guessing at when a candidate could actually start.
              ...(displaySession.linkOpensAt
                ? [{ label: 'Candidate can start from', value: fmtDate(displaySession.linkOpensAt) }] : []),
              ...(displaySession.linkClosesAt
                ? [{ label: 'Link expires',             value: fmtDate(displaySession.linkClosesAt) }] : []),
              { label: 'Interviewer', value: displaySession.interviewer ? `${displaySession.interviewer.firstName || ''} ${displaySession.interviewer.lastName || ''}`.trim() : '—' },
              ...(displaySession.startedAt   ? [{ label: 'Started',   value: fmtDate(displaySession.startedAt)   }] : []),
              ...(displaySession.completedAt ? [{ label: 'Completed', value: fmtDate(displaySession.completedAt) }] : []),
              ...(displaySession.totalScore != null ? [{ label: 'Score', value: `${displaySession.totalScore}%` }] : []),
              ...(displaySession.difficultyName    ? [{ label: 'Difficulty', value: displaySession.difficultyName    }] : []),
              ...(displaySession.interviewTypeName ? [{ label: 'Type',       value: displaySession.interviewTypeName }] : []),
              ...(displaySession.durationMinutes   ? [{ label: 'Duration',   value: `${displaySession.durationMinutes} min` }] : []),
            ].map(({ label, value }) => (
              <div key={label}>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
                <p className="text-sm text-foreground mt-0.5 break-words">{value}</p>
              </div>
            ))}

            {/* Regenerate button — only for scheduled sessions */}
            {isScheduled && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full gap-1.5 text-xs mt-2"
                onClick={() => setShowRegenModal(true)}
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Regenerate Questions
              </Button>
            )}
          </div>

          {/* Right: questions panel ──────────────────────────────────────────── */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Questions header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/20 flex-shrink-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Questions
              </p>
              <Badge variant="secondary" className="tabular-nums text-[11px]">
                {questions.length}
              </Badge>
            </div>

            {/* Questions list */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
              {questions.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full py-12 text-center text-muted-foreground gap-2">
                  <Hash className="h-8 w-8 opacity-30" />
                  <p className="text-sm">No questions found for this session.</p>
                  {isScheduled && (
                    <p className="text-xs">Use "Regenerate Questions" to generate them.</p>
                  )}
                </div>
              ) : (
                questions.map((q, idx) => (
                  <div key={q._id || idx}
                    className="rounded-lg border border-border bg-background p-3 space-y-2">
                    {/* Question number + type badge */}
                    <div className="flex items-start gap-2">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary mt-0.5">
                        {idx + 1}
                      </span>
                      <p className="text-sm text-foreground leading-snug flex-1">
                        {q.questionText}
                      </p>
                    </div>
                    {/* Badges */}
                    <div className="flex flex-wrap gap-1.5 pl-7">
                      <Badge variant="secondary" className="text-[10px] font-normal">{q.questionType}</Badge>
                      <Badge variant="outline"   className="text-[10px] font-normal">{q.difficulty}</Badge>
                      <Badge variant="muted"     className="text-[10px] font-normal flex items-center gap-1">
                        <Clock className="h-2.5 w-2.5" />
                        {q.timeLimitSeconds}s
                      </Badge>
                      {q.questionFormat === 'mcq' && (
                        <Badge className="text-[10px] font-normal bg-violet-500/10 text-violet-700 dark:text-violet-400 border-violet-500/20">
                          MCQ
                        </Badge>
                      )}
                      {q.questionType === 'coding' && q.language && (
                        <Badge variant="outline" className="text-[10px] font-mono font-normal">{q.language}</Badge>
                      )}
                    </div>
                    {/* MCQ options */}
                    {q.questionFormat === 'mcq' && Array.isArray(q.options) && q.options.length > 0 && (
                      <div className="pl-7 space-y-1">
                        {q.options.map((opt, oi) => (
                          <div key={oi} className={`flex items-center gap-2 rounded-md px-2 py-1 text-[11px] ${
                            opt.isCorrect
                              ? 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300 border border-emerald-500/20'
                              : 'bg-muted/40 text-muted-foreground'
                          }`}>
                            <span className="shrink-0 font-bold">{String.fromCharCode(65 + oi)}.</span>
                            <span>{opt.text}</span>
                            {opt.isCorrect && <span className="ml-auto shrink-0 font-semibold text-emerald-700 dark:text-emerald-400">✓ correct</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* ── Footer ──────────────────────────────────────────────────────────── */}
        <div className="flex-shrink-0 space-y-3 border-t border-border px-6 py-3">
          <ResendInvitePanel
            session={displaySession}
            onSent={(data) => setLocalSession(sx => ({
              ...sx,
              inviteSentAt: data.inviteSentAt,
              inviteResendCount: data.inviteResendCount,
            }))}
          />
          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={onClose}>Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <ReGenerateQuestionsModal
      open={showRegenModal}
      onClose={() => setShowRegenModal(false)}
      session={displaySession}
      onRegenerated={handleRegenerated}
    />
    </>
  );
}

const PAGE_SIZE = 10;

const STATUS_TABS = [
  { value: 'all',          label: 'All',          filter: '' },
  { value: 'scheduled',    label: 'Scheduled',    filter: 'scheduled' },
  { value: 'in_progress',  label: 'In progress',  filter: 'in_progress' },
  { value: 'disconnected', label: 'Disconnected', filter: 'disconnected' },
  { value: 'completed',    label: 'Completed',    filter: 'completed' },
  { value: 'expired',      label: 'Expired',      filter: 'expired' },
  // Started but cut short — proctoring caught something, or the candidate
  // closed the window. Auto-rejects the candidate's process, so it is worth
  // being able to review these as a group.
  { value: 'terminated',   label: 'Terminated',   filter: 'terminated' },
  { value: 'cancelled',    label: 'Cancelled',    filter: 'cancelled' },
  // Nothing writes no_show any more — a candidate who does not turn up now
  // expires instead. The tab exists so the historical records stay reachable.
  { value: 'no_show',      label: 'No show',      filter: 'no_show' },
];

const TYPE_LABELS = {
  coding: 'Coding',
  technical: 'Technical',
  behavioral: 'Behavioral',
  mixed: 'Mixed',
};

function formatScheduleWhen(iso) {
  if (!iso) return { date: '—', time: '', relative: '', tone: 'muted' };
  const d = new Date(iso);
  return {
    // Rendered in the browser's own zone, which is what the person reading the
    // screen is working in. Locale pinned so the order does not shift per user.
    date: d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
    time: d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }),
    ...relativeWhen(d),
  };
}

/**
 * How far away an interview is, in words.
 *
 * Compared by calendar day rather than elapsed hours: an interview at 9am
 * tomorrow is "Tomorrow" even though it is only fourteen hours away, which is
 * how someone reading a schedule actually thinks about it.
 */
function relativeWhen(d) {
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(d) - startOfDay(new Date())) / 86400000);

  if (days === 0)  return { relative: 'Today',     tone: 'now' };
  if (days === 1)  return { relative: 'Tomorrow',  tone: 'soon' };
  if (days === -1) return { relative: 'Yesterday', tone: 'past' };
  if (days > 1  && days <= 14) return { relative: `in ${days} days`,   tone: days <= 3 ? 'soon' : 'muted' };
  if (days < -1 && days >= -14) return { relative: `${-days} days ago`, tone: 'past' };
  return { relative: '', tone: 'muted' };
}

const WHEN_TONE = {
  now:  'text-emerald-600 dark:text-emerald-500 font-semibold',
  soon: 'text-amber-600 dark:text-amber-500 font-medium',
  past: 'text-muted-foreground',
  muted:'text-muted-foreground',
};

/** Small date/time block, used on both the group row and each round row. */
function WhenCell({ iso, compact }) {
  const w = formatScheduleWhen(iso);
  if (!iso) return <span className="text-[11px] text-muted-foreground">Not scheduled</span>;
  return (
    <div className="leading-tight">
      <p className={cn('text-[11px] font-medium text-foreground', compact && 'text-[11px]')}>{w.date}</p>
      <p className="text-[10px] text-muted-foreground">{w.time}</p>
      {w.relative && <p className={cn('text-[10px]', WHEN_TONE[w.tone])}>{w.relative}</p>}
    </div>
  );
}

// ── Group sessions by candidate + JD ─────────────────────────────────────────
export function groupSessionsByCandidate(sessions) {
  const map = new Map();
  for (const s of sessions) {
    const cid = s.candidate?._id || s.candidate || 'unknown';
    const tid = s.template?._id || s.template || 'unknown';
    // Group key: candidate + JD so the same candidate under different roles = separate rows
    const key = `${cid}__${tid}`;
    if (!map.has(key)) {
      map.set(key, {
        candidateId: cid,
        templateId: tid,
        name: `${s.candidate?.firstName || ''} ${s.candidate?.lastName || ''}`.trim() || 'Unknown',
        email: s.candidate?.email || '',
        role: s.template?.title || '—',
        interviewer: s.interviewer ? `${s.interviewer.firstName} ${s.interviewer.lastName}` : '—',
        sessions: [],
      });
    }
    map.get(key).sessions.push(s);
  }
  // Sort each group's sessions by roundNumber (blueprint order), then by date
  // within a round — a round attempted twice reads oldest-first, so a retry
  // after an expiry sits under the attempt it replaced.
  for (const g of map.values()) {
    g.sessions.sort((a, b) => {
      if (a.roundNumber != null && b.roundNumber != null && a.roundNumber !== b.roundNumber) {
        return a.roundNumber - b.roundNumber;
      }
      return new Date(a.scheduledAt) - new Date(b.scheduledAt);
    });

    // The row leads with the next interview still to happen, falling back to
    // the most recent one. "When is this candidate next due" is the question
    // being asked of this screen; the latest past round does not answer it.
    const now = Date.now();
    const upcoming = g.sessions
      .filter(s => s.scheduledAt && s.status === 'scheduled' && new Date(s.scheduledAt).getTime() >= now)
      .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
    g.primarySession = upcoming[0]
      || [...g.sessions].sort((a, b) => new Date(b.scheduledAt) - new Date(a.scheduledAt))[0];
    g.isUpcoming = upcoming.length > 0;

    // The status of the interview this row is already showing the date for.
    //
    // It used to be picked by scanning a fixed priority list across every
    // session in the group, where 'expired' outranked 'completed'. So a round
    // that expired and was rescheduled successfully still read Expired
    // forever, while the expanded rows underneath said Completed — the same
    // screen contradicting itself. Ten candidate groups were displaying a
    // status their newest attempt disagreed with.
    //
    // Deriving it from primarySession means the date and the status always
    // describe the same interview, and a superseded attempt stays history
    // rather than becoming the headline.
    g.status = g.primarySession?.status || 'scheduled';
    // latestDate = most recently scheduled session across all rounds in this group
    g.latestDate = g.sessions.reduce((max, s) => {
      const t = s.scheduledAt ? new Date(s.scheduledAt).getTime() : 0;
      return t > max ? t : max;
    }, 0);
  }
  // Sort groups: most recently scheduled first
  return [...map.values()].sort((a, b) => b.latestDate - a.latestDate);
}

function ScheduleInterviewPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canSchedule = hasPermission(user, 'schedule_interviews');
  // Cancel is restricted to admin and hr_manager only (not interviewer)
  const canCancel = canSchedule && (user?.role === 'admin' || user?.role === 'hr_manager');

  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [templateFilter, setTemplateFilter] = useState('');
  const [search, setSearch] = useState('');
  const [expandedCandidates, setExpandedCandidates] = useState(new Set()); // candidateIds that are open
  const prefilledCandidateId = searchParams.get('candidateId') || '';
  const prefilledProcessId = searchParams.get('processId') || '';
  const prefilledQuestionSetId = searchParams.get('questionSetId') || '';
  const [prefilledTemplateId, setPrefilledTemplateId] = useState('');
  const [prefilledCandidateFromProcess, setPrefilledCandidateFromProcess] = useState('');
  /**
   * Scheduling moved to its own page — a candidate table for eighty applicants
   * does not fit in a dialog. Callers that used to open the dialog with
   * parameters are forwarded there with the same ones, so every existing entry
   * point keeps working.
   */
  const goToSchedule = useCallback((extra = {}) => {
    const qs = new URLSearchParams();
    const carry = {
      templateId:    prefilledTemplateId,
      candidateId:   prefilledCandidateId || prefilledCandidateFromProcess,
      processId:     prefilledProcessId,
      questionSetId: prefilledQuestionSetId,
      ...extra,
    };
    for (const [k, v] of Object.entries(carry)) if (v) qs.set(k, v);
    navigate(`/admin/schedule/new${qs.toString() ? `?${qs}` : ''}`);
  }, [navigate, prefilledTemplateId, prefilledCandidateId, prefilledCandidateFromProcess,
      prefilledProcessId, prefilledQuestionSetId]);

  // Arriving with parameters used to auto-open the dialog; forward instead.
  useEffect(() => {
    if (prefilledCandidateId || prefilledProcessId || prefilledQuestionSetId) goToSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledCandidateId, prefilledProcessId, prefilledQuestionSetId]);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [resetSession, setResetSession] = useState(null);      // session being reset
  const [resetAction, setResetAction] = useState('allow_rejoin'); // 'allow_rejoin' | 'reschedule'
  const [resetDate, setResetDate] = useState('');
  const [resetTime, setResetTime] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    if (!prefilledProcessId) return;
    apiClient.getCandidateProcess(prefilledProcessId)
      .then(res => {
        const p = res.data;
        if (p?.templateId?._id) setPrefilledTemplateId(p.templateId._id);
        else if (p?.templateId) setPrefilledTemplateId(String(p.templateId));
        const cid = p?.candidateId?._id || p?.candidateId;
        if (cid) setPrefilledCandidateFromProcess(String(cid));
      })
      .catch(() => {});
  }, [prefilledProcessId]);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  const activeStatus = STATUS_TABS.find((t) => t.value === statusFilter)?.filter ?? '';

  const fetchData = useCallback(
    async (tableOnly = false) => {
      if (tableOnly) setTableLoading(true);
      else setLoading(true);
      try {
        const [statsRes, sessionsRes, templatesRes, candidatesRes] = await Promise.all([
          apiClient.getDashboardStats(),
          apiClient.getAllSessions(
            page,
            PAGE_SIZE,
            activeStatus || undefined,
            templateFilter || undefined,
            search.trim() || undefined
          ),
          apiClient.getTemplates(),
          apiClient.getCandidates(),
        ]);
        setStats(statsRes.data);
        setSessions(sessionsRes.data || []);
        setTotalPages(sessionsRes.pagination?.totalPages || 1);
        setTotalRecords(sessionsRes.pagination?.totalRecords ?? sessionsRes.data?.length ?? 0);
        setTemplates(templatesRes.data || []);
        setCandidates(candidatesRes.data || []);
        setError('');
      } catch {
        setError('Failed to load schedule data.');
        setSessions([]);
        setTotalPages(1);
        setTotalRecords(0);
      } finally {
        setLoading(false);
        setTableLoading(false);
      }
    },
    [page, activeStatus, templateFilter, search]
  );

  useEffect(() => {
    fetchData(page > 1 || activeStatus || templateFilter || search);
  }, [fetchData]);

  // Search is server-side (`q` param) so pagination doesn't hide matches.
  const filteredSessions = sessions;

  const groupedCandidates = useMemo(() => groupSessionsByCandidate(filteredSessions), [filteredSessions]);

  const toggleCandidate = (key) => {
    setExpandedCandidates(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleCreateSuccess = () => {
    setPage(1);
    fetchData();
  };

  const handleEdit = (session, e) => {
    e?.stopPropagation();
    setSelectedSession(session);
    setOpenEditDialog(true);
  };

  /**
   * Sends the user to the scheduling page with this candidate's details already
   * filled in. A fresh session is created rather than reviving the old one, so
   * the missed attempt stays on the record.
   */
  const handleRescheduleExpired = (session, e) => {
    e?.stopPropagation?.();
    const params = new URLSearchParams();
    const templateId  = session.template?._id  || session.template  || session.templateId;
    const candidateId = session.candidate?._id || session.candidate;
    if (templateId)         params.set('templateId', String(templateId));
    if (candidateId)        params.set('candidateId', String(candidateId));
    if (session.processId)  params.set('processId', String(session.processId));
    if (session.questionSetId) params.set('questionSetId', String(session.questionSetId));
    navigate(`/admin/schedule/new?${params.toString()}`);
  };

  const handleCancel = (session, e) => {
    e?.stopPropagation();
    setSelectedSession(session);
    setOpenCancelDialog(true);
  };

  const handleOpenReset = (session, action, e) => {
    e?.stopPropagation();
    setResetSession(session);
    setResetAction(action);
    const now = new Date(Date.now() + 60 * 60 * 1000); // default: 1h from now
    setResetDate(toDateInputValue(now));
    setResetTime(toTimeInputValue(now));
    setOpenResetDialog(true);
  };

  const handleConfirmReset = async () => {
    if (!resetSession) return;
    setResetLoading(true);
    try {
      const payload = { action: resetAction };
      if (resetAction === 'reschedule') {
        const dt = new Date(`${resetDate}T${resetTime}`);
        payload.scheduledAt = dt.toISOString();
      }
      await apiClient.resetSession(resetSession._id, payload);
      setOpenResetDialog(false);
      setResetSession(null);
      fetchData(true);
    } catch (err) {
      setError(err.message || 'Failed to reset session.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleView = async (session) => {
    try {
      const response = await apiClient.getSessionDetailsForAdmin(session._id);
      setSelectedSession(response.data);
      setOpenViewDialog(true);
    } catch {
      setError('Failed to load session details.');
    }
  };

  if (loading && !stats) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-md" />
          ))}
        </div>
        <Skeleton className="h-64 w-full rounded-md" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Schedule</h1>
          <p className="text-[11px] text-muted-foreground">Plan and manage interview sessions</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={() => fetchData(true)}
            disabled={tableLoading}
          >
            <Loader2 className={cn('h-3.5 w-3.5', tableLoading && 'animate-spin')} />
            Refresh
          </Button>
          {canSchedule && (
          <Button size="sm" className="h-8 gap-1.5 shadow-sm" onClick={() => goToSchedule()}>
            <CalendarPlus className="h-3.5 w-3.5" />
            Schedule
          </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <span>{error}</span>
          <button type="button" className="text-xs font-medium underline" onClick={() => setError('')}>
            Dismiss
          </button>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
          <DashboardKpiCard compact title="Scheduled" value={stats.scheduled ?? 0} icon={Calendar} tone="primary" />
          <DashboardKpiCard compact title="In progress" value={stats.inProgress ?? 0} icon={PlayCircle} tone="warning" />
          <DashboardKpiCard compact title="Completed" value={stats.completed ?? 0} icon={CheckCircle2} tone="success" />
          <DashboardKpiCard compact title="All sessions" value={stats.total ?? 0} icon={ListTodo} tone="default" />
        </div>
      )}

      <Card className="overflow-hidden rounded-md border-border shadow-sm">
        <CardHeader className="space-y-2 border-b border-border/60 bg-muted/10 px-3 py-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <CardTitle className="shrink-0 text-sm font-semibold">Interview sessions</CardTitle>
            <span className="text-[10px] tabular-nums text-muted-foreground">
              {totalRecords} {totalRecords === 1 ? 'candidate' : 'candidates'} · Page {page} of {totalPages}
            </span>
            <Tabs
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v);
                setPage(1);
              }}
              className="min-w-0 flex-1 sm:ml-auto sm:flex-none"
            >
              <TabsList className="flex h-7 max-w-full flex-wrap gap-0.5 p-0.5">
                {STATUS_TABS.map((t) => (
                  <TabsTrigger key={t.value} value={t.value} className="h-6 px-2 text-[11px] data-[state=active]:shadow-sm">
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <div className="relative min-w-[10rem] flex-1">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search candidate, email, role…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 pl-7 text-xs"
              />
              {search && (
                <button
                  type="button"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <NativeSelect
              id="sched-template-filter"
              // label="Role"
              value={templateFilter}
              onChange={(e) => {
                setTemplateFilter(e.target.value);
                setPage(1);
              }}
              className="w-[9.5rem] shrink-0 space-y-0.5 [&_label]:mb-0 [&_label]:text-[10px] [&_select]:h-8 [&_select]:py-1 [&_select]:text-xs"
            >
              <option value="">All roles</option>
              {templates.map((t) => (
                <option key={t._id} value={t._id}>
                  {t.title}
                </option>
              ))}
            </NativeSelect>
            {(templateFilter || search) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 shrink-0 px-2 text-xs"
                onClick={() => {
                  setTemplateFilter('');
                  setSearch('');
                  setPage(1);
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="relative p-0">
          {tableLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}
          <Table className="text-xs [&_td]:px-3 [&_td]:py-2 [&_th]:px-3 [&_th]:py-2">
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead className="w-8" />
                <TableHead>Candidate</TableHead>
                <TableHead>When</TableHead>
                <TableHead className="hidden sm:table-cell">Role</TableHead>
                <TableHead className="hidden md:table-cell">Interviewer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Rounds</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groupedCandidates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-28 text-center">
                    <p className="text-sm text-muted-foreground">No interviews match your filters.</p>
                    <Button size="sm" className="mt-2 h-8 gap-1.5 text-xs" onClick={() => goToSchedule()}>
                      <CalendarPlus className="h-3.5 w-3.5" />
                      Schedule first interview
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                groupedCandidates.map((group) => {
                  const groupKey = `${group.candidateId}__${group.templateId}`;
                  const expanded = expandedCandidates.has(groupKey);
                  const multiRound = group.sessions.length > 1;
                  return (
                    <React.Fragment key={groupKey}>
                      {/* ── Candidate group header row ────────────────── */}
                      <TableRow
                        className={cn(
                          'cursor-pointer select-none transition-colors hover:bg-muted/40',
                          expanded && 'bg-muted/20 border-b-0'
                        )}
                        onClick={() => multiRound ? toggleCandidate(groupKey) : handleView(group.sessions[0])}
                      >
                        {/* Expand chevron */}
                        <TableCell className="w-8 pr-0">
                          {multiRound ? (
                            <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform duration-200', expanded && 'rotate-180')} />
                          ) : (
                            <span className="block h-3.5 w-3.5" />
                          )}
                        </TableCell>

                        {/* Name + email */}
                        <TableCell>
                          <p className="font-semibold text-foreground">{group.name}</p>
                          <p className="text-[11px] text-muted-foreground">{group.email || '—'}</p>
                          <p className="text-[11px] text-muted-foreground sm:hidden truncate">{group.role}</p>
                        </TableCell>

                        {/* When — the next round still to come, else the latest */}
                        <TableCell>
                          <WhenCell iso={group.primarySession?.scheduledAt} />
                          {multiRound && group.isUpcoming && (
                            <p className="text-[9px] uppercase tracking-wide text-muted-foreground">next round</p>
                          )}
                        </TableCell>

                        {/* Role */}
                        <TableCell className="hidden sm:table-cell max-w-[12rem] truncate">
                          {group.role}
                        </TableCell>

                        {/* Interviewer */}
                        <TableCell className="hidden md:table-cell text-[11px] text-muted-foreground">
                          {group.interviewer}
                        </TableCell>

                        {/* Dominant status */}
                        <TableCell>
                          <StatusBadge status={group.status} />
                        </TableCell>

                        {/* Round count */}
                        <TableCell className="text-right">
                          {multiRound ? (
                            <Badge variant="secondary" className="tabular-nums text-[10px] gap-1">
                              <Hash className="h-2.5 w-2.5" />
                              {group.sessions.length} rounds
                            </Badge>
                          ) : (
                            <div className="flex justify-end gap-0.5">
                              <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs"
                                onClick={(e) => { e.stopPropagation(); handleView(group.sessions[0]); }}>
                                <Eye className="h-3 w-3" />View
                              </Button>
                              {canSchedule && group.sessions[0].status === 'scheduled' && (
                                <Button variant="ghost" size="icon" className="h-7 w-7"
                                  onClick={(e) => handleEdit(group.sessions[0], e)} aria-label="Edit">
                                  <Pencil className="h-3 w-3 text-warning" />
                                </Button>
                              )}
                              {canSchedule && ['expired', 'no_show'].includes(group.sessions[0].status) && (
                                <Button variant="ghost" size="icon" className="h-7 w-7"
                                  onClick={(e) => handleRescheduleExpired(group.sessions[0], e)}
                                  aria-label="Reschedule" title="Schedule a fresh session for this candidate">
                                  <Calendar className="h-3 w-3 text-blue-500" />
                                </Button>
                              )}
                              {canCancel && ['scheduled', 'expired'].includes(group.sessions[0].status) && (
                                <Button variant="ghost" size="icon" className="h-7 w-7"
                                  onClick={(e) => handleCancel(group.sessions[0], e)} aria-label="Cancel interview">
                                  <XCircle className="h-3 w-3 text-destructive" />
                                </Button>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>

                      {/* ── Expanded round rows ───────────────────────── */}
                      {expanded && group.sessions.map((session, ri) => {
                        return (
                          <TableRow key={session._id}
                            className="bg-muted/10 hover:bg-muted/20 border-l-2 border-l-primary/20">
                            {/* indent */}
                            <TableCell className="text-center text-muted-foreground/40 pr-0">
                              <div className="flex h-full items-center justify-center">
                                <div className="w-px h-4 bg-border mx-auto" />
                              </div>
                            </TableCell>

                            {/* Round label */}
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className={cn(
                                  'shrink-0 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider',
                                  session.status === 'completed'    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' :
                                  session.status === 'in_progress'  ? 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400' :
                                  session.status === 'disconnected' ? 'border-orange-500/30 bg-orange-500/10 text-orange-700 dark:text-orange-400' :
                                  session.status === 'expired'      ? 'border-orange-500/30 bg-orange-500/10 text-orange-700 dark:text-orange-400' :
                                  session.status === 'cancelled'    ? 'border-destructive/30 bg-destructive/10 text-destructive' :
                                  session.status === 'terminated'   ? 'border-destructive/30 bg-destructive/10 text-destructive' :
                                  session.status === 'no_show'      ? 'border-orange-500/30 bg-orange-500/10 text-orange-700 dark:text-orange-400' :
                                  'border-primary/20 bg-primary/5 text-primary'
                                )}>
                                  {session.roundName || (session.roundNumber != null ? `Round ${session.roundNumber}` : `Session ${ri + 1}`)}
                                </span>
                              </div>
                            </TableCell>

                            {/* When — same column as the group row above */}
                            <TableCell>
                              <WhenCell iso={session.scheduledAt} compact />
                            </TableCell>

                            {/* Template */}
                            <TableCell className="hidden sm:table-cell max-w-[12rem] truncate text-[11px] text-muted-foreground">
                              <div>
                                <p className="truncate">{session.template?.title || '—'}</p>
                                {session.interviewTypeName && (
                                  <Badge variant="outline" className="mt-0.5 text-[9px] font-normal">
                                    {session.interviewTypeName}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>

                            {/* Interviewer */}
                            <TableCell className="hidden md:table-cell text-[11px] text-muted-foreground">
                              {session.interviewer ? `${session.interviewer.firstName} ${session.interviewer.lastName}` : '—'}
                            </TableCell>

                            {/* Status */}
                            <TableCell>
                              <StatusBadge status={session.status} />
                              {session.totalScore != null && (
                                <p className="mt-0.5 text-[10px] font-semibold text-primary tabular-nums">{Math.round(session.totalScore)}%</p>
                              )}
                            </TableCell>

                            {/* Actions */}
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-0.5">
                                <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs"
                                  onClick={(e) => { e.stopPropagation(); handleView(session); }}>
                                  <Eye className="h-3 w-3" />View
                                </Button>
                                {canSchedule && session.status === 'scheduled' && (
                                  <Button variant="ghost" size="icon" className="h-7 w-7"
                                    onClick={(e) => handleEdit(session, e)} aria-label="Edit">
                                    <Pencil className="h-3 w-3 text-warning" />
                                  </Button>
                                )}
                                {canSchedule && ['expired', 'no_show'].includes(session.status) && (
                                  <Button variant="ghost" size="sm"
                                    className="h-7 gap-1 px-2 text-[10px] text-blue-600 hover:text-blue-500"
                                    onClick={(e) => handleRescheduleExpired(session, e)}
                                    title="Schedule a fresh session for this candidate">
                                    <Calendar className="h-3 w-3" />Reschedule
                                  </Button>
                                )}
                                {canCancel && ['scheduled', 'expired'].includes(session.status) && (
                                  <Button variant="ghost" size="icon" className="h-7 w-7"
                                    onClick={(e) => handleCancel(session, e)} aria-label="Cancel interview">
                                    <XCircle className="h-3 w-3 text-destructive" />
                                  </Button>
                                )}
                                {canCancel && ['disconnected', 'in_progress'].includes(session.status) && (
                                  <>
                                    <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-[10px] text-amber-600 hover:text-amber-500"
                                      onClick={(e) => handleOpenReset(session, 'allow_rejoin', e)} title="Allow candidate to rejoin">
                                      <RefreshCw className="h-3 w-3" />Rejoin
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-[10px] text-blue-600 hover:text-blue-500"
                                      onClick={(e) => handleOpenReset(session, 'reschedule', e)} title="Reschedule fresh session">
                                      <Calendar className="h-3 w-3" />Reschedule
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>

          <div className="border-t border-border/60 px-3">
            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={totalRecords}
              itemLabel={totalRecords === 1 ? 'candidate' : 'candidates'}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </div>
        </CardContent>
      </Card>


      <EditInterviewDialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setSelectedSession(null);
        }}
        session={selectedSession}
        templates={templates}
        candidates={candidates}
        onUpdate={() => {
          setOpenEditDialog(false);
          setSelectedSession(null);
          fetchData(true);
        }}
      />

      <CancelInterviewDialog
        open={openCancelDialog}
        onClose={() => {
          setOpenCancelDialog(false);
          setSelectedSession(null);
        }}
        session={selectedSession}
        onCancel={() => {
          setOpenCancelDialog(false);
          setSelectedSession(null);
          fetchData(true);
        }}
      />

      <ViewInterviewDialog
        open={openViewDialog}
        onClose={() => {
          setOpenViewDialog(false);
          setSelectedSession(null);
        }}
        session={selectedSession}
        onRegenerated={(updatedSession) => {
          // Update the selected session reference so questions count in the table
          // refreshes after the modal closes
          setSelectedSession(updatedSession);
          // Also refresh the sessions table in the background
          fetchData(true);
        }}
      />

      {/* ── Reset (Rejoin / Reschedule) dialog ─────────────────────────── */}
      <Dialog open={openResetDialog} onOpenChange={(o) => { if (!o) { setOpenResetDialog(false); setResetSession(null); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <WifiOff className="h-4 w-4 text-orange-500" />
              <DialogTitle>
                {resetAction === 'allow_rejoin' ? 'Allow Candidate to Rejoin' : 'Reschedule Fresh Session'}
              </DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Candidate info */}
            {resetSession && (
              <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm">
                <p className="font-medium text-foreground">
                  {resetSession.candidate?.firstName} {resetSession.candidate?.lastName}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {resetSession.roundName || `Round ${resetSession.roundNumber || 1}`} · Status: <span className="font-medium text-orange-600">{resetSession.status}</span>
                </p>
                {resetSession.rescheduleRequested && (
                  <p className="mt-1 text-xs text-amber-600 font-medium">⚠ Candidate requested a reschedule</p>
                )}
              </div>
            )}

            {resetAction === 'allow_rejoin' ? (
              <p className="text-sm text-muted-foreground">
                The session will be reset to <strong>Scheduled</strong>. The candidate can log back in and resume from where they left off. All previously answered questions are preserved.
              </p>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  The current session will be cancelled and a fresh session created with new questions. Set the new date and time:
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Date</Label>
                    <Input type="date" value={resetDate} onChange={e => setResetDate(e.target.value)} className="h-8 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Time</Label>
                    <Input type="time" value={resetTime} onChange={e => setResetTime(e.target.value)} className="h-8 text-sm" />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => { setOpenResetDialog(false); setResetSession(null); }}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={resetLoading || (resetAction === 'reschedule' && (!resetDate || !resetTime))}
              onClick={handleConfirmReset}
              className={resetAction === 'allow_rejoin' ? 'bg-amber-600 hover:bg-amber-500' : ''}
            >
              {resetLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
              {resetAction === 'allow_rejoin' ? 'Reset & Allow Rejoin' : 'Reschedule'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ScheduleInterviewPage;
